import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { useDebounce } from '../../../hooks/useDebounce';
// Публічний /lib/met-activities: автокомпліт використовують і клієнти/експерти (власне тренування).
import { metActivityService, MetActivity } from '../../../services/met-activity.service';

interface MetActivityAutocompleteProps {
  value: MetActivity | null;
  onChange: (activity: MetActivity | null) => void;
  label?: string;
  helperText?: string;
}

export const MetActivityAutocomplete: React.FC<MetActivityAutocompleteProps> = ({
  value,
  onChange,
  label = "Пошук MET активності",
  helperText = "Почніть вводити назву або код"
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<MetActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const fetchSuggestions = useCallback(async (search: string) => {
    if (search.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await metActivityService.getActivities({ search, limit: 20 });
      setOptions(response.data);
    } catch (error) {
      console.error("Failed to fetch MET activities:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchSuggestions(debouncedSearchTerm);
    } else {
      setOptions([]);
    }
  }, [debouncedSearchTerm, fetchSuggestions]);

  useEffect(() => {
    // When the component receives a new value from props, update the input field
    if (value) {
      setInputValue(value.name ? `${value.code} - ${value.name}` : '');
    } else {
      setInputValue('');
    }
  }, [value]);

  return (
    <Autocomplete
      id="met-activity-autocomplete"
      value={value}
      onChange={(_event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        // Only update inputValue state if the user types or clears,
        // preventing automatic resets that conflict with selection
        if (reason === 'input' || reason === 'clear') {
           setInputValue(newInputValue);
        } else if (reason === 'reset' && value) {
           // When selection happens ('reset'), ensure format is kept
           setInputValue(value.name ? `${value.code} - ${value.name}` : '');
        }
      }}
      options={options}
      loading={loading}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(option) => option.name ? `${option.code} - ${option.name}` : ''}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">
              <strong>{option.code}</strong> - {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MET: {option.metValue} | {option.categoryName}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
